import { useRef } from 'react';

import { useFlag } from '@/lib/api';
import { ENV, isInternalEnvironment } from '@/lib/config';

// TODO: swap the flag-based gate below for a permission check —
// import { Permission, usePermissions } from '@/hooks/usePermissions';

/**
 * THE SINGLE ACCESS SEAM FOR THE GROUND TRUTH HUB.
 *
 * This hook is the ONLY place GT-hub access policy lives. Every gate — the
 * route shell, the sidebar nav entry, and any future GT surface — must consult
 * this hook rather than re-deriving the policy. Centralising it here means the
 * team can flip the hub's visibility rule in exactly one spot.
 *
 * SAFE DEFAULT (current): the hub is hidden on customer tenants and
 * prospect-facing environments, and stays off everywhere unless the
 * `ground_truth_hub` feature flag is explicitly enabled. "Internal" is decided
 * by `isInternalEnvironment` in `@/lib/config`, which covers local dev plus the
 * shared `dev`/`staging`/`preprod` stack. It does NOT mirror the backend's
 * `is_dev`.
 *
 * NOTE: feature flags are synced from the license server. The `ground_truth_hub`
 * flag must be created and enabled in the license server admin UI before the hub
 * becomes accessible on dev (the flag defaults to false when absent).
 *
 * LATER: the team will swap the final `canAccess` line to a permission check.
 * The one-line alternative is:
 *
 *   const { hasPermission } = usePermissions();
 *   const canAccess = hasPermission([Permission.ManageGroundTruth]);
 *
 * Until then, keep all GT access decisions flowing through this hook.
 */
export function useGroundTruthAccess(): {
    canAccess: boolean;
    isLoading: boolean;
} {
    const isInternal = isInternalEnvironment(ENV);
    const { data: flag, isError } = useFlag('ground_truth_hub', isInternal);

    // Once granted, a later transient `false` refetch must not revoke it mid-session.
    const grantedRef = useRef(false);
    if (flag === true) grantedRef.current = true;

    const canAccess = isInternal && (flag === true || grantedRef.current);

    // Pending counts as loading only where the query runs; elsewhere decides immediately.
    const isLoading = isInternal && flag === undefined && !isError;

    // LATER (permission-based): replace the line above with —
    // const { hasPermission } = usePermissions();
    // const canAccess = hasPermission([Permission.ManageGroundTruth]);

    return { canAccess, isLoading };
}
